// Phase 4 #1 — Match Result Modal.
// Admins / leadership / captains use this to record:
//   - Final scores (homeScore + awayScore, both free-text since the
//     format varies per sport: "187/6" for cricket, "3-2" for volleyball)
//   - Outcome (Win / Loss / Draw / Tie / Abandoned)
//   - Notes (free text)
//   - Top performers — sport-specific picker:
//       Cricket    → Best Batter(s), Best Bowler(s), Top Performer (1)
//       Volleyball → Top Performer(s)
//
// Saves via PUT /api/v1/matches/:id/result. Re-opens cleanly with
// previously-saved data so admins can edit/refine.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Save, Trophy, Award, Hand, Sparkles, Loader2, Check,
  CheckCircle2, XCircle, MinusCircle, CircleDot, CloudRain,
  CalendarDays, MapPin
} from "lucide-react";
import { useToast } from "./Toast";
import { api } from "../api";

const OUTCOMES = [
  { value: "WIN",       label: "Win",       color: "#5ee3a1", Icon: CheckCircle2 },
  { value: "LOSS",      label: "Loss",      color: "#ff6b6b", Icon: XCircle },
  { value: "DRAW",      label: "Draw",      color: "#9ba6c4", Icon: MinusCircle },
  { value: "TIE",       label: "Tie",       color: "#ff8a2a", Icon: CircleDot },
  { value: "ABANDONED", label: "Abandoned", color: "#9ba6c4", Icon: CloudRain }
];

function isCricket(sport) { return (sport || "").toLowerCase() === "cricket"; }

// Deterministic gradient + initials for a player chip avatar.
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #5ea8ff, #1f5da0)",
  "linear-gradient(135deg, #c897ff, #6b3ea0)",
  "linear-gradient(135deg, #5ce1e6, #1f7e8a)",
  "linear-gradient(135deg, #ff8a2a, #b04a00)",
  "linear-gradient(135deg, #5ee3a1, #1a8f56)",
  "linear-gradient(135deg, #ff6b6b, #a52a2a)"
];
function avatarFor(userId, name) {
  const idx = Math.abs(Number(userId) || 0) % AVATAR_GRADIENTS.length;
  const initials = (name || "?")
    .split(/\s+/).slice(0, 2).map(s => s[0] || "").join("").toUpperCase() || "?";
  return { bg: AVATAR_GRADIENTS[idx], initials };
}

function CategoryPicker({ title, icon, roster, selected, onToggle, multi = true, hint, accent }) {
  const selectedCount = selected.size;
  return (
    <div className="resultModal-catBlock">
      <header>
        <span className="resultModal-catTitle" style={accent ? { color: accent } : null}>
          {icon} {title}
          {selectedCount > 0 && (
            <span className="resultModal-catCount" style={accent ? { background: `${accent}26`, color: accent } : null}>
              {selectedCount}
            </span>
          )}
        </span>
        {hint && <small>{hint}</small>}
      </header>
      <div className="resultModal-catGrid">
        {roster.length === 0 && (
          <em className="resultModal-catEmpty">
            No squad / roster on file. Publish a squad for this match first.
          </em>
        )}
        {roster.map(p => {
          const picked = selected.has(p.id);
          const av = avatarFor(p.id, p.name);
          return (
            <button
              key={p.id}
              type="button"
              className={`resultModal-catChip ${picked ? "is-picked" : ""}`}
              onClick={() => onToggle(p.id, multi)}
              aria-pressed={picked}
              style={picked && accent ? { borderColor: accent, color: accent, background: `${accent}1f` } : null}
            >
              <span className="resultModal-catChipAvatar" style={{ background: av.bg }}>
                {picked ? <Check size={11}/> : av.initials}
              </span>
              {p.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MatchResultModal({ match, team, roster, onClose, onSaved }) {
  const toast = useToast();
  const cricket = isCricket(match?.sport);

  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [homeScore, setHomeScore] = useState("");
  const [awayScore, setAwayScore] = useState("");
  const [outcome,   setOutcome]   = useState("");
  const [notes,     setNotes]     = useState("");

  // Selected user IDs per category — Sets for clean toggling.
  const [batters,   setBatters]    = useState(() => new Set());
  const [bowlers,   setBowlers]    = useState(() => new Set());
  const [tops,      setTops]       = useState(() => new Set());

  // Load existing result on open so edits round-trip.
  useEffect(() => {
    if (!match?.id) return;
    let cancelled = false;
    setLoading(true);
    api.getMatchResult(match.id)
      .then(r => {
        if (cancelled) return;
        setHomeScore(r.homeScore || "");
        setAwayScore(r.awayScore || "");
        setOutcome(r.outcome     || "");
        setNotes(r.notes         || "");
        const b = new Set(), w = new Set(), t = new Set();
        for (const h of (r.highlights || [])) {
          if (h.category === "BEST_BATTER")   b.add(h.userId);
          if (h.category === "BEST_BOWLER")   w.add(h.userId);
          if (h.category === "TOP_PERFORMER") t.add(h.userId);
        }
        setBatters(b); setBowlers(w); setTops(t);
      })
      .catch(() => { /* first time — leave blanks */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [match?.id]);

  function toggle(setter, multi) {
    return (id) => setter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (!multi) next.clear();
        next.add(id);
      }
      return next;
    });
  }

  const homeLabel = team?.name?.replace(/(Cricket|Volleyball|Team|Club)/gi, "").trim() || "Home";
  const awayLabel = match?.opponent || "Away";

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      // Build the highlights array (sport-aware).
      const highlights = [];
      if (cricket) {
        for (const uid of batters) highlights.push({ userId: uid, category: "BEST_BATTER"   });
        for (const uid of bowlers) highlights.push({ userId: uid, category: "BEST_BOWLER"   });
        for (const uid of tops)    highlights.push({ userId: uid, category: "TOP_PERFORMER" });
      } else {
        for (const uid of tops)    highlights.push({ userId: uid, category: "TOP_PERFORMER" });
      }
      const saved = await api.saveMatchResult(match.id, {
        homeScore: homeScore.trim() || null,
        awayScore: awayScore.trim() || null,
        outcome:   outcome || null,
        notes:     notes.trim() || null,
        highlights
      });
      toast.success("Match result saved.");
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast.error(err.message || "Couldn't save match result.");
    } finally { setSaving(false); }
  }

  const pickedOutcome = OUTCOMES.find(o => o.value === outcome);

  // Render at document.body via a portal so the V3 sticky topbar
  // (which lives inside a stacking context) can never sit on top of
  // the modal. This also frees us from any ancestor transform/filter
  // that would otherwise constrain `position: fixed`.
  return createPortal((
    <div className="resultModal" onClick={onClose}>
      <div className="resultModal-inner" onClick={e => e.stopPropagation()}>
        {/* ── Sticky scoreboard header ─── */}
        <header className="resultModal-head">
          <button type="button" className="resultModal-close" onClick={onClose} aria-label="Close">
            <X size={18}/>
          </button>
          <div className="resultModal-eyebrowTop">
            <Trophy size={12}/> Match Result · {match?.sport}
          </div>
          <div className="resultModal-scoreboard">
            <div className="resultModal-team">
              <strong>{homeLabel}</strong>
              <span className="resultModal-teamLabel">Home</span>
            </div>
            <div className="resultModal-vs">
              <span>vs</span>
              {pickedOutcome && (
                <span
                  className="resultModal-outcomeBadge"
                  style={{ background: `${pickedOutcome.color}22`, color: pickedOutcome.color, borderColor: pickedOutcome.color }}
                >
                  <pickedOutcome.Icon size={12}/> {pickedOutcome.label.toUpperCase()}
                </span>
              )}
            </div>
            <div className="resultModal-team resultModal-team--away">
              <strong>{awayLabel}</strong>
              <span className="resultModal-teamLabel">Opponent</span>
            </div>
          </div>
          <div className="resultModal-meta">
            {match?.date && <span><CalendarDays size={11}/> {match.date}</span>}
            {match?.venue && <span><MapPin size={11}/> {match.venue}</span>}
          </div>
        </header>

        {loading ? (
          <div className="resultModal-loading">
            <Loader2 size={24} className="resultModal-spin"/>
            Loading existing result…
          </div>
        ) : (
          <>
            {/* ── Scrollable body ─── */}
            <div className="resultModal-body">
              {/* SCORES */}
              <section className="resultModal-section">
                <div className="resultModal-sectionH">Final Score</div>
                <div className="resultModal-scoreRow">
                  <label className="resultModal-scoreField">
                    <span>{homeLabel}</span>
                    <input
                      value={homeScore}
                      onChange={e => setHomeScore(e.target.value)}
                      placeholder={cricket ? "187/6" : "3"}
                      maxLength={60}
                    />
                  </label>
                  <div className="resultModal-scoreDash">—</div>
                  <label className="resultModal-scoreField resultModal-scoreField--away">
                    <span>{awayLabel}</span>
                    <input
                      value={awayScore}
                      onChange={e => setAwayScore(e.target.value)}
                      placeholder={cricket ? "183/9" : "2"}
                      maxLength={60}
                    />
                  </label>
                </div>
              </section>

              {/* OUTCOME */}
              <section className="resultModal-section">
                <div className="resultModal-sectionH">Outcome</div>
                <div className="resultModal-outcomeBtns">
                  {OUTCOMES.map(o => {
                    const picked = outcome === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        className={`resultModal-outcomeBtn ${picked ? "is-picked" : ""}`}
                        style={picked ? {
                          borderColor: o.color,
                          color: o.color,
                          background: `${o.color}1a`,
                          boxShadow: `0 0 0 3px ${o.color}26`
                        } : null}
                        onClick={() => setOutcome(picked ? "" : o.value)}
                      >
                        <o.Icon size={13}/> {o.label}
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* NOTES */}
              <section className="resultModal-section">
                <div className="resultModal-sectionH">Notes (optional)</div>
                <textarea
                  className="resultModal-textarea"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. Last over thriller, rain interrupted, etc."
                  maxLength={500}
                />
              </section>

              {/* TOP PERFORMERS */}
              <section className="resultModal-section">
                <div className="resultModal-sectionH resultModal-sectionH--gold">
                  <Sparkles size={12}/> Top Performers
                </div>
                {cricket ? (
                  <>
                    <CategoryPicker
                      title="Best Batter(s)"
                      icon={<Trophy size={13}/>}
                      roster={roster}
                      selected={batters}
                      onToggle={toggle(setBatters, true)}
                      hint="Pick 1 or more"
                      accent="#ff8a2a"
                    />
                    <CategoryPicker
                      title="Best Bowler(s)"
                      icon={<Hand size={13}/>}
                      roster={roster}
                      selected={bowlers}
                      onToggle={toggle(setBowlers, true)}
                      hint="Pick 1 or more"
                      accent="#5ea8ff"
                    />
                    <CategoryPicker
                      title="Top Performer of the Match"
                      icon={<Award size={13}/>}
                      roster={roster}
                      selected={tops}
                      onToggle={toggle(setTops, false)}
                      hint="Pick exactly 1"
                      multi={false}
                      accent="var(--gold)"
                    />
                  </>
                ) : (
                  <CategoryPicker
                    title="Top Performer(s)"
                    icon={<Award size={13}/>}
                    roster={roster}
                    selected={tops}
                    onToggle={toggle(setTops, true)}
                    hint="Pick 1 or more"
                    accent="var(--gold)"
                  />
                )}
              </section>
            </div>

            {/* ── Sticky footer ─── */}
            <footer className="resultModal-foot">
              <button type="button" className="dark" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="button" className="btn" onClick={save} disabled={saving}>
                {saving
                  ? <><Loader2 size={14} className="resultModal-spin"/> Saving…</>
                  : <><Save size={14}/> Save Result</>}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  ), document.body);
}
